"use client";

import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { Play } from "lucide-react";

import { renderInlineMath } from "@/app/components/quizzes/render-math";
import { event } from "@/lib/gtag";
import {
  buildVideoAnalyticsParams,
  createVideoPlaybackId,
  VIDEO_PROGRESS_MILESTONES,
  type VideoAnalyticsContext,
} from "@/lib/video/analytics";
import type { VideoTranscript } from "@/lib/video/transcripts";
import type { H2StudyRoute } from "@/lib/study/h2-study-types";
import { saveStudyProgress } from "@/lib/study/study-progress";

import {
  VIDEO_WATCH_ENGLISH_COPY,
  type VideoWatchCopy,
} from "@/lib/video/watch-copy";
export { VIDEO_WATCH_ENGLISH_COPY } from "@/lib/video/watch-copy";

type VideoWatchExperienceProps = {
  copy?: VideoWatchCopy;
  showAllHighlights?: boolean;
  title: string;
  highlights: readonly string[];
  mp4: string;
  poster: string;
  transcript: VideoTranscript | null;
  analytics: Omit<VideoAnalyticsContext, "sourceSurface" | "playerSurface">;
  studyRoute?: H2StudyRoute | null;
};

type TranscriptCue = VideoTranscript["cues"][number];

type TranscriptChapter = {
  id: string;
  label: string | null;
  cues: { cue: TranscriptCue; transcriptIndex: number }[];
};

type TranscriptLayout = "panel" | "focus";
type DetailPanel = "overview" | "transcript";

function groupTranscriptCues(cues: TranscriptCue[]): TranscriptChapter[] {
  return cues.reduce<TranscriptChapter[]>((chapters, cue, transcriptIndex) => {
    const label = cue.label?.trim() || null;
    const previous = chapters.at(-1);

    if (previous && previous.label === label) {
      previous.cues.push({ cue, transcriptIndex });
      return chapters;
    }

    chapters.push({
      id: `transcript-chapter-${chapters.length + 1}`,
      label,
      cues: [{ cue, transcriptIndex }],
    });
    return chapters;
  }, []);
}

function formatCueTime(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  const rounded = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(rounded / 60);
  const secs = rounded % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function parseTimeHash(): number | null {
  if (typeof window === "undefined") return null;
  const match = window.location.hash.match(/^#t=(\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function buildTimeHash(seconds: number): string {
  return `#t=${Math.max(0, Math.floor(seconds))}`;
}

export default function VideoWatchExperience({
  title,
  highlights,
  mp4,
  poster,
  transcript,
  analytics,
  studyRoute = null,
  copy = VIDEO_WATCH_ENGLISH_COPY,
  showAllHighlights = false,
}: VideoWatchExperienceProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playbackIdRef = useRef("");
  if (!playbackIdRef.current) playbackIdRef.current = createVideoPlaybackId();
  const hasViewedRef = useRef(false);
  const hasStartedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const hasErroredRef = useRef(false);
  const hasOpenedTranscriptRef = useRef(false);
  const progressMilestonesRef = useRef<Set<number>>(new Set());
  const maxProgressRef = useRef(0);
  const watchedSecondsRef = useRef(0);
  const lastMediaTimeRef = useRef<number | null>(null);
  const nativeSeekFromRef = useRef<number | null>(null);
  const suppressNativeSeekRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [pendingHashTime, setPendingHashTime] = useState<number | null>(null);
  const [transcriptLayout, setTranscriptLayout] =
    useState<TranscriptLayout>("panel");
  const [activePanel, setActivePanel] = useState<DetailPanel>("overview");

  const hasAlignedTiming = transcript?.timingStatus === "whisperx-aligned";
  const transcriptChapters = transcript
    ? groupTranscriptCues(transcript.cues)
    : [];
  const overviewHighlights = showAllHighlights
    ? highlights
    : highlights.slice(0, 3);

  useEffect(() => {
    const requestedLayout = new URLSearchParams(window.location.search).get(
      "transcriptLayout"
    );
    if (requestedLayout === "focus") {
      setTranscriptLayout("focus");
    }

    const hashTime = parseTimeHash();
    if (hashTime == null) return;
    setActivePanel("transcript");
    hasOpenedTranscriptRef.current = true;
    event("video_transcript_opened", {
      ...buildVideoEventParams(),
      transcript_layout: requestedLayout === "focus" ? "focus" : "panel",
      open_method: "deep_link",
    });
    const video = videoRef.current;
    if (video && video.readyState >= 1) {
      video.currentTime = hashTime;
      setCurrentTime(hashTime);
      return;
    }
    setPendingHashTime(hashTime);
    // Deep-link initialization is mount-only. Route analytics metadata does not
    // change during a mounted video experience.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildVideoEventParams(progressPercent?: number) {
    return {
      ...buildVideoAnalyticsParams(
        {
          ...analytics,
          sourceSurface: "video_page_player",
          playerSurface: "video_page",
        },
        playbackIdRef.current,
        {
          elapsedSeconds: Math.round(watchedSecondsRef.current),
          maxProgressPercent: Math.round(maxProgressRef.current),
          progressPercent,
        }
      ),
      video_url: mp4,
      player_muted: videoRef.current?.muted ?? false,
    };
  }

  function resetPlaybackAttempt() {
    playbackIdRef.current = createVideoPlaybackId();
    hasStartedRef.current = false;
    hasCompletedRef.current = false;
    hasErroredRef.current = false;
    progressMilestonesRef.current.clear();
    maxProgressRef.current = 0;
    watchedSecondsRef.current = 0;
    lastMediaTimeRef.current = 0;
  }

  function handleLoadedMetadata() {
    if (!hasViewedRef.current) {
      hasViewedRef.current = true;
      event("video_viewed", buildVideoEventParams());
    }

    if (pendingHashTime == null) return;
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = pendingHashTime;
    setCurrentTime(pendingHashTime);
    setPendingHashTime(null);
  }

  function handlePlay() {
    if (hasCompletedRef.current) {
      resetPlaybackAttempt();
      event("video_viewed", buildVideoEventParams());
      event("video_replayed", buildVideoEventParams());
    }
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    event("video_started", buildVideoEventParams());
    // Transitional compatibility for existing GA4 reports.
    event("video_play", buildVideoEventParams());
    if (studyRoute) {
      saveStudyProgress({
        subject: studyRoute.subject,
        subjectLabel: studyRoute.subjectLabel,
        topicId: studyRoute.topicId,
        topicLabel: studyRoute.topicLabel,
        activity: "video",
        resumeHref: analytics.sourcePage,
        noteHref: studyRoute.noteHref,
        quizHref: studyRoute.quizHref,
        videoHref: analytics.sourcePage,
      });
    }
  }

  function handleEnded() {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    maxProgressRef.current = 100;
    event("video_completed", buildVideoEventParams(100));
    // Transitional compatibility for existing GA4 reports.
    event("video_complete", buildVideoEventParams());
  }

  function handleTimeUpdate(eventObject: SyntheticEvent<HTMLVideoElement>) {
    const video = eventObject.currentTarget;
    setCurrentTime(video.currentTime);

    const previousMediaTime = lastMediaTimeRef.current;
    if (
      previousMediaTime != null &&
      !video.seeking &&
      !video.paused &&
      video.currentTime >= previousMediaTime
    ) {
      const delta = video.currentTime - previousMediaTime;
      if (delta <= 5) watchedSecondsRef.current += delta;
    }
    lastMediaTimeRef.current = video.currentTime;

    const duration = analytics.videoDurationSeconds ?? video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const progress = Math.min(100, (video.currentTime / duration) * 100);
    maxProgressRef.current = Math.max(maxProgressRef.current, progress);
    for (const milestone of VIDEO_PROGRESS_MILESTONES) {
      if (progress < milestone) continue;
      if (progressMilestonesRef.current.has(milestone)) continue;

      progressMilestonesRef.current.add(milestone);
      event("video_progress", buildVideoEventParams(milestone));
    }
  }

  function handleTranscriptSeek(cue: VideoTranscript["cues"][number]) {
    if (cue.startSeconds == null || !Number.isFinite(cue.startSeconds)) return;
    const video = videoRef.current;
    if (!video) return;

    const seekFromSeconds = video.currentTime;
    suppressNativeSeekRef.current = true;
    video.currentTime = cue.startSeconds;
    lastMediaTimeRef.current = cue.startSeconds;
    setCurrentTime(cue.startSeconds);

    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", buildTimeHash(cue.startSeconds));
    }

    event("video_transcript_seek", {
      ...buildVideoEventParams(),
      cue_id: cue.cueId,
      cue_label: cue.label ?? undefined,
      seek_seconds: cue.startSeconds,
    });
    event("video_seeked", {
      ...buildVideoEventParams(),
      seek_method: "transcript",
      seek_from_seconds: seekFromSeconds,
      seek_to_seconds: cue.startSeconds,
      seek_direction:
        cue.startSeconds >= seekFromSeconds ? "forward" : "backward",
      cue_id: cue.cueId,
      cue_label: cue.label ?? undefined,
    });

    void video.play();
  }

  function handleSeeking(eventObject: SyntheticEvent<HTMLVideoElement>) {
    if (suppressNativeSeekRef.current) return;
    nativeSeekFromRef.current = lastMediaTimeRef.current;
    lastMediaTimeRef.current = eventObject.currentTarget.currentTime;
  }

  function handleSeeked(eventObject: SyntheticEvent<HTMLVideoElement>) {
    if (suppressNativeSeekRef.current) {
      suppressNativeSeekRef.current = false;
      return;
    }

    const seekFromSeconds = nativeSeekFromRef.current;
    const seekToSeconds = eventObject.currentTarget.currentTime;
    nativeSeekFromRef.current = null;
    lastMediaTimeRef.current = seekToSeconds;
    if (seekFromSeconds == null || seekFromSeconds === seekToSeconds) return;

    event("video_seeked", {
      ...buildVideoEventParams(),
      seek_method: "controls",
      seek_from_seconds: seekFromSeconds,
      seek_to_seconds: seekToSeconds,
      seek_direction: seekToSeconds >= seekFromSeconds ? "forward" : "backward",
    });
  }

  function handleError() {
    if (hasErroredRef.current) return;
    hasErroredRef.current = true;
    event("video_error", {
      ...buildVideoEventParams(),
      error_stage: "playback",
      media_error_code: videoRef.current?.error?.code,
    });
  }

  function isActiveCue(cue: VideoTranscript["cues"][number]): boolean {
    if (!hasAlignedTiming || cue.startSeconds == null) return false;
    const start = cue.startSeconds;
    const end = cue.endSeconds ?? start + 3;
    return currentTime >= start && currentTime < end;
  }

  function showPanel(panel: DetailPanel) {
    setActivePanel(panel);
    if (panel === "transcript" && !hasOpenedTranscriptRef.current) {
      hasOpenedTranscriptRef.current = true;
      event("video_transcript_opened", {
        ...buildVideoEventParams(),
        transcript_layout: transcriptLayout,
        open_method: "panel_control",
      });
    }
    event("video_detail_panel_view", {
      ...buildVideoEventParams(),
      detail_panel: panel,
      transcript_layout: transcriptLayout,
    });
  }

  const overviewPanel = (
    <section
      id="video-overview-panel"
      aria-labelledby={
        transcriptLayout === "panel" ? "video-overview-tab" : undefined
      }
      role={transcriptLayout === "panel" ? "tabpanel" : undefined}
      className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7 md:p-8"
    >
      {overviewHighlights.length > 0 ? (
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">
            {copy.learn}
          </h2>
          <ul className="mt-3 space-y-2.5 text-sm leading-6 text-slate-700 sm:text-base sm:leading-7">
            {overviewHighlights.map((highlight, index) => (
              <li key={index} className="grid grid-cols-[0.5rem_1fr] gap-3">
                <span
                  aria-hidden="true"
                  className="mt-[0.65rem] h-1.5 w-1.5 rounded-full bg-blue-600"
                />
                <span>
                  {renderInlineMath(highlight, `video-highlight-${index}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {transcriptLayout === "focus" && transcript ? (
        <button
          type="button"
          onClick={() => showPanel("transcript")}
          className="mt-7 inline-flex min-h-11 items-center justify-center rounded-full bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          {copy.focus}
        </button>
      ) : null}
    </section>
  );

  const transcriptPanel = transcript ? (
    <section
      id="video-transcript-panel"
      aria-labelledby={
        transcriptLayout === "panel" ? "video-transcript-tab" : undefined
      }
      role={transcriptLayout === "panel" ? "tabpanel" : undefined}
      className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
    >
      {transcriptLayout === "focus" ? (
        <div className="flex justify-end px-5 pt-5 sm:px-8 sm:pt-6">
          <button
            type="button"
            onClick={() => showPanel("overview")}
            className="inline-flex min-h-10 items-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            {copy.back}
          </button>
        </div>
      ) : null}

      <div className="px-5 sm:px-8 md:max-h-[calc(100vh-15rem)] md:overflow-y-auto">
        {transcriptChapters.map((chapter, chapterIndex) => (
          <section
            key={chapter.id}
            aria-labelledby={chapter.label ? chapter.id : undefined}
            className="border-t border-slate-100 py-8 first:border-t-0 sm:py-10"
          >
            {chapter.label ? (
              <div className="mb-5 flex items-center gap-3 sm:mb-6">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-xs font-bold tabular-nums text-blue-700 ring-1 ring-inset ring-blue-100">
                  {String(chapterIndex + 1).padStart(2, "0")}
                </span>
                <h3
                  id={chapter.id}
                  className="text-base font-semibold tracking-tight text-slate-950 sm:text-lg"
                >
                  {chapter.label}
                </h3>
              </div>
            ) : null}

            <ol className="space-y-1.5">
              {chapter.cues.map(({ cue, transcriptIndex }) => {
                const time = formatCueTime(cue.startSeconds);
                const active = isActiveCue(cue);

                return (
                  <li
                    key={cue.cueId}
                    data-active={active ? "true" : undefined}
                    className={`group relative grid grid-cols-1 items-start gap-2 overflow-hidden rounded-xl px-3 py-3 transition-colors before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-transparent sm:grid-cols-[5rem_1fr] sm:gap-4 sm:px-3.5 sm:py-3.5 ${
                      active
                        ? "bg-blue-50/80 before:bg-blue-600"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div>
                      {time ? (
                        <button
                          type="button"
                          onClick={() => handleTranscriptSeek(cue)}
                          aria-current={active ? "true" : undefined}
                          className={`inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                            active
                              ? "border-blue-300 bg-white text-blue-800 shadow-sm"
                              : "border-blue-200 bg-blue-50/50 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                          }`}
                          aria-label={copy.jump.replace("{time}", time)}
                        >
                          <Play
                            className="h-3 w-3 fill-current"
                            aria-hidden="true"
                          />
                          {time}
                        </button>
                      ) : (
                        <span className="px-1.5 text-xs font-medium tabular-nums text-slate-400">
                          {String(transcriptIndex + 1).padStart(2, "0")}
                        </span>
                      )}
                    </div>
                    <p
                      className={`max-w-[42rem] text-[0.9375rem] leading-6 sm:text-base sm:leading-7 ${
                        active ? "font-medium text-slate-900" : "text-slate-600"
                      }`}
                    >
                      {renderInlineMath(
                        cue.displayText,
                        `video-transcript-${transcriptIndex}`
                      )}
                    </p>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </section>
  ) : null;

  return (
    <div
      data-transcript-layout={transcriptLayout}
      data-active-panel={activePanel}
      className={`grid items-start gap-6 md:gap-x-6 md:gap-y-6 xl:gap-x-10 ${
        transcriptLayout === "focus" && activePanel === "transcript"
          ? "md:grid-cols-[minmax(13rem,17rem)_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)]"
          : "md:grid-cols-[minmax(17rem,23rem)_minmax(0,1fr)] xl:grid-cols-[26rem_minmax(0,1fr)]"
      }`}
    >
      <header className="order-1 py-1 md:col-start-2 md:row-start-1 md:self-end">
        <h1 className="text-[1.75rem] font-semibold leading-tight tracking-tight text-slate-950 sm:text-3xl xl:text-4xl">
          {renderInlineMath(title, "video-title")}
        </h1>
      </header>

      <figure
        aria-label={copy.video.replace("{title}", title)}
        className="order-2 mx-auto w-full max-w-[23rem] md:sticky md:top-6 md:col-start-1 md:row-span-3 md:row-start-1 md:max-w-none"
      >
        <div className="aspect-[9/16] overflow-hidden rounded-3xl bg-slate-950 shadow-sm ring-1 ring-slate-900/10">
          {/* The adjacent timed transcript provides the current text alternative. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            poster={poster}
            className="h-full w-full object-cover"
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={handlePlay}
            onEnded={handleEnded}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onSeeked={handleSeeked}
            onError={handleError}
          >
            <source src={mp4} type="video/mp4" />
          </video>
        </div>
      </figure>

      {transcriptLayout === "panel" && transcript ? (
        <div
          className="order-3 grid grid-cols-2 rounded-full bg-slate-100 p-1 md:col-start-2 md:row-start-2"
          role="tablist"
          aria-label={copy.details}
        >
          <button
            id="video-overview-tab"
            type="button"
            role="tab"
            aria-selected={activePanel === "overview"}
            aria-controls="video-overview-panel"
            onClick={() => showPanel("overview")}
            className={`min-h-11 rounded-full px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
              activePanel === "overview"
                ? "bg-white text-slate-950 shadow-sm"
                : "text-slate-600 hover:text-slate-950"
            }`}
          >
            {copy.overview}
          </button>
          <button
            id="video-transcript-tab"
            type="button"
            role="tab"
            aria-selected={activePanel === "transcript"}
            aria-controls="video-transcript-panel"
            onClick={() => showPanel("transcript")}
            className={`min-h-11 rounded-full px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
              activePanel === "transcript"
                ? "bg-white text-slate-950 shadow-sm"
                : "text-slate-600 hover:text-slate-950"
            }`}
          >
            {copy.transcript}
          </button>
        </div>
      ) : null}

      <div
        className={`${
          transcriptLayout === "panel"
            ? "order-4 md:row-start-3"
            : "order-3 md:row-start-2"
        } md:col-start-2`}
      >
        {activePanel === "overview" ? overviewPanel : transcriptPanel}
      </div>
    </div>
  );
}
